/**
 * MultiBuy Builder - product.js
 * Product card rendering (grid + bottom-sheet contexts) and the Quick View
 * drawer. Variant handling is fully dynamic: reads product.options at
 * runtime, never assumes "Size" is the option name.
 * Attaches to window.MB.product
 */
(function (window, document) {
  'use strict';

  var MB = (window.MB = window.MB || {});
  var utils = MB.utils;
  var discount = MB.discount;
  var store = MB.store;

  /** Find the variant matching an ordered array of option values. */
  function pickVariant(product, selectedOptions) {
    return (
      product.variants.filter(function (v) {
        return v.options.every(function (val, idx) {
          return !selectedOptions[idx] || val === selectedOptions[idx];
        });
      })[0] || product.variants[0]
    );
  }

  function firstAvailableOptionValues(product) {
    var variant = product.variants.filter(function (v) { return v.available; })[0] || product.variants[0];
    return variant ? variant.options.slice() : [];
  }

  // Remembers a shopper's explicit chip pick per bundle+product, so it
  // survives the full grid re-render that fires on every bundle:changed
  // (renderCard is called fresh each time, with no other memory of what
  // was clicked) - keyed by bundle since the same product can appear in
  // more than one bundle's grid with a different size chosen in each.
  // Written by collection.js's handleChipClick (live chip clicks) and
  // ensureAutoFilled (curated packs' auto-selected slots).
  var explicitSelections = {};
  function selectionKey(bundleId, productId) {
    return (bundleId || '') + ':' + productId;
  }
  function setSelectedOption(bundleId, productId, value) {
    explicitSelections[selectionKey(bundleId, productId)] = value;
  }
  function getSelectedOption(bundleId, productId) {
    return explicitSelections[selectionKey(bundleId, productId)];
  }

  function previewDiscountPercent(ctx) {
    if (!ctx) return 0;
    var tier = discount.getTier(ctx.rules, discount.countItems(ctx.items) + 1);
    return tier ? tier.discount_percent : 0;
  }

  /** The tier the bundle has actually already earned right now (no +1) -
   * unlike previewDiscountPercent, which is an "add one more" enticement
   * for cards not yet in the bundle. */
  function currentDiscountPercent(ctx) {
    if (!ctx) return 0;
    var tier = discount.getTier(ctx.rules, discount.countItems(ctx.items));
    return tier ? tier.discount_percent : 0;
  }

  /**
   * Renders a product card for the create-your-own grid / bottom sheet grid.
   * mode: 'add' (create-your-own, "Add" button) | 'select' (sheet, "Select" button)
   */
  function renderCard(product, ctx, mode) {
    var bundleId = ctx ? ctx.id : null;
    // A shopper's explicit chip pick (persisted across the full
    // re-render every bundle:changed triggers - see setSelectedOption)
    // wins over the auto-resolved default so the chosen size actually
    // sticks once shown again, both on the chip itself and on the
    // variant actually used for pricing/data-variant-id.
    var explicitValue = getSelectedOption(bundleId, product.id);
    var hasExplicitSelection = explicitValue != null;
    var selectedOptions = hasExplicitSelection ? [explicitValue] : firstAvailableOptionValues(product);
    var variant = pickVariant(product, selectedOptions);
    var firstOption = product.options[0];
    var alreadyAdded = !!(ctx && store.isSelected(ctx.id, product.id));
    // Already-added cards show the tier the bundle has actually earned
    // right now, not the "add one more" +1 preview - that preview is
    // meant to entice a card that ISN'T in the bundle yet, but applied
    // to one that already is, it made an untouched card's price visibly
    // shift every time a completely different card got added/removed
    // elsewhere in the grid, which read as its price randomly changing.
    var previewPct = alreadyAdded ? currentDiscountPercent(ctx) : previewDiscountPercent(ctx);
    var effectivePrice = variant.price; // Bundle discounts are shown only in the bundle summary.

    // A real choice only exists when there's more than one value to
    // pick from - a single-size product has nothing to choose, so it
    // shouldn't be blocked waiting for a selection that can't happen.
    var requiresChoice = !!(firstOption && firstOption.values && firstOption.values.length > 1);

    var chipsHtml = '';
    if (requiresChoice) {
      chipsHtml =
        '<div class="mb-chip-group"><p>Select Size</p><div class="mb-chip-row" data-mb-option-index="0">' +
        firstOption.values
          .map(function (val) {
            // Only shows selected once the shopper has actually chosen
            // a size (hasExplicitSelection) - never pre-selected on
            // first render, so Select stays disabled below until they do.
            var isSelected = hasExplicitSelection && selectedOptions[0] === val;
            var variantForVal = pickVariant(product, [val]);
            var disabled = !variantForVal || !variantForVal.available;
            return (
              '<button type="button" class="mb-chip' + (isSelected ? ' mb-chip--selected' : '') + (disabled ? ' mb-chip--disabled' : '') + '" ' +
              'data-mb-chip-value="' + utils.escapeHtml(val) + '"' + (disabled ? ' disabled' : '') + '>' +
              utils.escapeHtml(val) +
              '</button>'
            );
          })
          .join('') +
        '</div></div>';
    }

    // firstAvailableOptionValues falls back to product.variants[0] only
    // when NOTHING is available, so !variant.available here means every
    // size is out of stock, not just the one currently resolved. A
    // missing-but-required size choice is NOT disabled here - clicking
    // without one shows a toast instead (see handleCardAction in
    // collection.js), so the shopper is told why rather than just
    // finding an inert button.
    var noneAvailable = !variant.available;

    // Select/Replace row now sits above the title, in the card body -
    // not overlaid on the photo. The row itself (checkbox + label) is
    // the add/remove toggle, replacing the old image-overlay fab.
    var selectRowHtml =
      '<div class="mb-product-card__select-row">' +
      '<button type="button" class="mb-product-card__select-toggle" data-mb-action="' + (mode === 'select' ? 'select-product' : 'add-product') + '"' +
      (noneAvailable && !alreadyAdded ? ' disabled' : '') +
      ' role="checkbox" aria-checked="' + (alreadyAdded ? 'true' : 'false') + '"' +
      ' aria-label="' + (alreadyAdded ? 'Remove' : mode === 'select' ? 'Select' : 'Add') + ' ' + utils.escapeHtml(product.title) + '">' +
      '<span class="mb-product-card__checkbox" aria-hidden="true">' +
      (alreadyAdded ? '<svg width="10" height="10" viewBox="0 0 16 16" fill="none"><path d="M3 8.5l3 3 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' : '') +
      '</span>' +
      '<span>' + (alreadyAdded ? 'Selected' : 'Select') + '</span>' +
      '</button>' +
      (mode === 'select' && alreadyAdded
        ? '<button type="button" class="mb-link mb-product-card__replace" data-mb-action="replace-product"><span class="mb-product-card__replace-icon" aria-hidden="true"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.67747 12L9.01467 11.3372L10.203 10.1489H4.21768C2.4087 10.1489 0.936971 8.67713 0.936971 6.86815V6H1.87432V6.86815C1.87432 8.1603 2.92555 9.21151 4.21768 9.21151H10.203L9.01467 8.02319L9.67747 7.36037L11.9973 9.68019L9.67747 12ZM11.0603 6H10.123V5.13185C10.123 3.8397 9.07173 2.78849 7.7796 2.78849H1.79427L2.98261 3.97681L2.31981 4.63963L0 2.31981L2.31981 0L2.98261 0.662774L1.79427 1.85114H7.7796C9.58858 1.85114 11.0603 3.32287 11.0603 5.13185V6Z" fill="#636AE8"/></svg></span>Replace <span class="selection-text">Selection</span></button>'
        : '') +
      '</div>';

    return (
      '<div class="mb-product-card' + (alreadyAdded ? ' mb-product-card--added' : '') + (mode === 'select' && !alreadyAdded ? ' mb-product-card--unselected' : '') + '" data-product-id="' + product.id + '" data-product-handle="' + product.handle + '" data-variant-id="' + variant.id + '">' +
      '<div class="mb-product-card__image-wrap">' +
      '<button type="button" class="mb-product-card__image-btn" data-mb-action="quick-view" data-handle="' + product.handle + '" aria-label="Quick view ' + utils.escapeHtml(product.title) + '">' +
      // (previewPct ? '<span class="mb-badge">' + previewPct + '% OFF</span>' : '') +
      '<img src="' + utils.escapeHtml(product.featured_image || '') + '" alt="' + utils.escapeHtml(product.title) + '" loading="lazy">' +
      '</button>' +
      '</div>' +
      '<div class="mb-product-card__body">' +
      selectRowHtml +
      '<button type="button" class="mb-product-card__title" data-mb-action="quick-view" data-handle="' + product.handle + '">' + utils.escapeHtml(product.title) + '</button>' +
      chipsHtml +
      '<div class="mb-product-card__price-row">' +
      '<span class="mb-price">' + utils.formatMoney(effectivePrice) + '</span>' +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }

  function renderGrid(products, ctx, mode) {
    if (!products.length) {
      return '<div class="mb-empty-state">No products match your search.</div>';
    }
    return products.map(function (p) { return renderCard(p, ctx, mode); }).join('');
  }

  // ---------------------------------------------------------------------
  // Quick View drawer
  // ---------------------------------------------------------------------

  var quickView = {
    el: null,
    product: null,
    selectedOptions: [],
    // Per option index: true once the shopper has actually clicked a
    // chip for it. selectedOptions still resolves a real default
    // variant (for price display) even before that happens - this is
    // what gates whether a chip LOOKS selected and whether Add to
    // Bundle can proceed, kept separate so "no explicit choice yet"
    // isn't confused with "resolved to some default behind the scenes".
    touchedOptions: {},
    quantity: 1,
    bundleId: null
  };

  function getDrawerEl() {
    if (!quickView.el) quickView.el = utils.qs('[data-mb-quick-view]');
    return quickView.el;
  }

  function getDrawerBackdropEl() {
    return utils.qs('[data-mb-quick-view-backdrop]');
  }

  function open(handle, bundleId) {
    var drawer = getDrawerEl();
    if (!drawer) return;
    quickView.bundleId = bundleId;

    drawer.classList.add('mb-drawer--loading');
    drawer.hidden = false;
    var backdrop = getDrawerBackdropEl();
    if (backdrop) backdrop.hidden = false;
    utils.lockScroll(true);
    requestAnimationFrame(function () {
      drawer.classList.add('mb-drawer--open');
      if (backdrop) backdrop.classList.add('mb-drawer-backdrop--visible');
    });

    MB.api.getProduct(handle).then(function (product) {
      quickView.product = product;
      quickView.selectedOptions = firstAvailableOptionValues(product);
      quickView.touchedOptions = {};
      quickView.quantity = 1;
      var pending = getQuickViewReplacement();
      if (pending) {
        var pendingVariant = product.variants.filter(function (v) { return v.id === pending.variantId; })[0];
        if (pendingVariant) {
          quickView.selectedOptions = pendingVariant.options.slice();
          pendingVariant.options.forEach(function (_, index) { quickView.touchedOptions[index] = true; });
          quickView.quantity = pending.quantity;
        }
      }
      drawer.classList.remove('mb-drawer--loading');
      renderQuickView();
    });
  }

  function close() {
    var drawer = getDrawerEl();
    if (!drawer) return;
    drawer.classList.remove('mb-drawer--open');
    var backdrop = getDrawerBackdropEl();
    if (backdrop) backdrop.classList.remove('mb-drawer-backdrop--visible');
    utils.lockScroll(false);
    // Matches (with a small buffer) the 0.3s transform transition on
    // .mb-drawer - see the same fix in collection.js's close().
    setTimeout(function () {
      drawer.hidden = true;
      if (backdrop) backdrop.hidden = true;
    }, 320);
  }

  function getQuickViewReplacement() {
    var pending = MB.collection && MB.collection.getPendingReplacement(quickView.bundleId);
    return pending && quickView.product && pending.productId === quickView.product.id ? pending : null;
  }

  function renderOrUpdateReplacement() {
    if (getQuickViewReplacement()) commitQuickViewSelection();
    else renderQuickView();
  }

  function renderQuickView() {
    var drawer = getDrawerEl();
    if (!drawer || !quickView.product) return;
    var product = quickView.product;
    var variant = pickVariant(product, quickView.selectedOptions);

    var qvCtx = quickView.bundleId ? store.getContext(quickView.bundleId) : null;
    var qvPending = getQuickViewReplacement();
    var qvAlreadyAdded = !!qvPending || !!(qvCtx && store.isSelected(qvCtx.id, product.id));

    // Back floats over the whole gallery area (top-left); the vertical
    // thumbnail rail sits to the left of the main carousel, and
    // share/selected-badge float over just the main image - see
    // mb-drawer__gallery-wrap/mb-qv-gallery-main.
    var galleryHtml =
      '<div class="mb-drawer__gallery-wrap">' +
      '<button type="button" class="mb-icon-btn mb-qv-back" data-mb-action="qv-close" aria-label="Close"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><!--!Font Awesome Free 7.3.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2026 Fonticons, Inc.--><path d="M169.4 297.4C156.9 309.9 156.9 330.2 169.4 342.7L361.4 534.7C373.9 547.2 394.2 547.2 406.7 534.7C419.2 522.2 419.2 501.9 406.7 489.4L237.3 320L406.6 150.6C419.1 138.1 419.1 117.8 406.6 105.3C394.1 92.8 373.8 92.8 361.3 105.3L169.3 297.3z"/></svg></button>' +
      '<div class="mb-qv-gallery-row">' +
      (product.images.length > 1
        ? '<div class="mb-qv-thumbs" data-mb-qv-thumbs>' +
          product.images
            .map(function (src, i) {
              return '<button type="button" class="mb-qv-thumb' + (i === 0 ? ' mb-qv-thumb--active' : '') + '" data-mb-qv-thumb data-index="' + i + '" aria-label="Show image ' + (i + 1) + '"><img src="' + utils.escapeHtml(src) + '" alt=""></button>';
            })
            .join('') +
          '</div>'
        : '') +
      '<div class="mb-qv-gallery-main">' +
      '<div class="mb-qv-gallery" data-mb-qv-gallery>' +
      (product.images.length
        ? product.images
            .map(function (src, i) {
              return '<img class="mb-qv-gallery__img' + (i === 0 ? ' mb-qv-gallery__img--active' : '') + '" src="' + utils.escapeHtml(src) + '" alt="">';
            })
            .join('')
        : '<div class="mb-qv-gallery__placeholder"></div>') +
      '</div>' +
      (product.images.length > 1
        ? '<div class="mb-qv-gallery__dots" data-mb-qv-dots>' +
          product.images
            .map(function (_, i) {
              return '<button type="button" class="mb-qv-gallery__dot' + (i === 0 ? ' mb-qv-gallery__dot--active' : '') + '" data-mb-qv-dot data-index="' + i + '" aria-label="Show image ' + (i + 1) + '"></button>';
            })
            .join('') +
          '</div>'
        : '') +
      (qvAlreadyAdded
        ? '<span class="mb-qv-selected-badge"><svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3 3 7-7" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>Selected</span>'
        : '') +
      '<button type="button" class="mb-icon-btn mb-qv-share-btn" data-mb-action="qv-share" aria-label="Share"><img src="https://cdn.shopify.com/s/files/1/0589/9268/9284/files/share-2.svg?v=1784365506" width="40px"></button>' +
      '</div>' +
      '</div>' +
      '</div>';

    var optionsHtml = product.options
      .map(function (option, index) {
        var values = option.values || [];
        if (values.length < 2) return '';
        return (
          '<div class="mb-qv-option" data-mb-option-index="' + index + '">' +
          '<p class="mb-qv-option__label">Select ' + utils.escapeHtml(option.name) + '</p>' +
          '<div class="mb-chip-row">' +
          values
            .map(function (val) {
              var testOptions = quickView.selectedOptions.slice();
              testOptions[index] = val;
              var candidate = pickVariant(product, testOptions);
              var disabled = !candidate || !candidate.available;
              var selected = !!quickView.touchedOptions[index] && quickView.selectedOptions[index] === val;
              return (
                '<button type="button" class="mb-chip' + (selected ? ' mb-chip--selected' : '') + (disabled ? ' mb-chip--disabled' : '') + '" ' +
                'data-mb-qv-option="' + index + '" data-mb-qv-value="' + utils.escapeHtml(val) + '"' + (disabled ? ' disabled' : '') + '>' +
                utils.escapeHtml(val) +
                '</button>'
              );
            })
            .join('') +
          '</div>' +
          '</div>'
        );
      })
      .join('');

    var previewPct = previewDiscountPercent(qvCtx);
    var effectivePrice = variant.price; // Bundle discounts are shown only in the bundle summary.

    // Once a product is in the bundle, this drawer's job is done - the
    // CTA becomes a back-navigation to the pack instead of a toggle, so
    // removing an item only happens from its checkbox/the footer, never
    // from here.
    // Only hard-disabled when every variant is genuinely out of stock -
    // an incomplete (not-yet-touched) selection is caught by the toast
    // in commitQuickViewSelection instead of disabling this outright, so
    // the shopper gets told why, not just left with an inert button.
    var noneAvailable = !product.variants.some(function (v) { return v.available; });
    var ctaHtml = qvAlreadyAdded
      ? '<button type="button" class="mb-btn mb-btn--primary mb-btn--block view-multi" data-mb-action="qv-view-pack">' +
        '<span class="mb-btn__label">&larr; View Multi Buy Pack</span>' +
        '</button>'
      : '<button type="button" class="mb-btn mb-btn--primary mb-btn--block view-bundle" data-mb-action="qv-add"' + (noneAvailable ? ' disabled' : '') + '>' +
        '<span class="mb-btn__label">Add to Bundle</span>' +
        '</button>';

    drawer.innerHTML =
     '<div class="cart-drawer">'+  galleryHtml +
      '<div class="mb-drawer__body">' +
      '<h3 class="mb-qv-title">' + utils.escapeHtml(product.title) + '</h3>' +
      (product.description ? '<div class="mb-qv-description">' + product.description + '</div>' : '') +
      '<div class="mb-qv-price-row">' +
      '<span class="mb-price mb-price--lg">' + utils.formatMoney(effectivePrice) + '</span>' +
      '</div>' +
      optionsHtml +
      '<div class="mb-qv-qty">' +
      '<p class="mb-qv-option__label">Quantity</p>' +
      '<div class="mb-stepper">' +
      '<button type="button" class="mb-icon-btn" data-mb-action="qv-qty-dec">&minus;</button>' +
      '<span class="mb-stepper__value">' + quickView.quantity + '</span>' +
      '<button type="button" class="mb-icon-btn" data-mb-action="qv-qty-inc">&#43;</button>' +
      '</div>' +
      '</div>' +
      '</div>' +
      '</div>' +
      '<div class="mb-drawer__footer">' +
      (qvAlreadyAdded
        ? '<div class="mb-qv-added-banner"><strong>' + utils.escapeHtml(product.title) + '</strong>' + (qvPending ? ' is ready to replace your selection' : ' is added to your Multi-Buy pack') + '</div>'
        : '') +
      ctaHtml +
      '</div>';

    bindGalleryCarousel(drawer);
  }

  /**
   * Dot pagination + left-hand thumbnail rail, both kept in sync with the
   * Quick View image gallery's scroll position - mirrors the exact same
   * pattern already used for the curated bundle slider (see initSlider
   * in multibuy.js): one full-width image at a time via scroll-snap,
   * dots/thumbnails click to jump to an index, and a debounced scroll
   * listener keeps both in sync while swiping/dragging. Rebinding on
   * every renderQuickView call is necessary since drawer.innerHTML is
   * rebuilt from scratch each time.
   */
  function bindGalleryCarousel(drawer) {
    var gallery = utils.qs('[data-mb-qv-gallery]', drawer);
    var dots = utils.qsa('[data-mb-qv-dot]', drawer);
    var thumbs = utils.qsa('[data-mb-qv-thumb]', drawer);
    if (!gallery || (!dots.length && !thumbs.length)) return;

    function slideWidth() {
      var img = utils.qs('.mb-qv-gallery__img', gallery);
      return img ? img.getBoundingClientRect().width : gallery.getBoundingClientRect().width;
    }

    function goToIndex(index) {
      gallery.scrollTo({ left: slideWidth() * index, behavior: 'smooth' });
    }

    function setActive(index) {
      dots.forEach(function (dot, i) {
        dot.classList.toggle('mb-qv-gallery__dot--active', i === index);
      });
      thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('mb-qv-thumb--active', i === index);
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        goToIndex(parseInt(dot.dataset.index, 10));
      });
    });
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        goToIndex(parseInt(thumb.dataset.index, 10));
      });
    });

    gallery.addEventListener(
      'scroll',
      utils.debounce(function () {
        var index = Math.round(gallery.scrollLeft / slideWidth());
        setActive(utils.clamp(index, 0, Math.max(dots.length, thumbs.length) - 1));
      }, 100)
    );
  }

  function bindQuickViewEvents() {
    document.addEventListener('click', function (e) {
      var viewPackBtn = e.target.closest('[data-mb-action="qv-view-pack"]');
      if (viewPackBtn) {
        close();
        var ctx = store.getContext(quickView.bundleId);
        if (ctx && ctx.replaceIndex != null) MB.collection.completeReplacement(ctx.id);
        return;
      }
      var closeBtn = e.target.closest('[data-mb-action="qv-close"]');
      if (closeBtn) {
        close();
        return;
      }

      var shareBtn = e.target.closest('[data-mb-action="qv-share"]');
      if (shareBtn && quickView.product) {
        utils.shareLink(window.location.origin + quickView.product.url, quickView.product.title);
        return;
      }

      var chip = e.target.closest('[data-mb-qv-option]');
      if (chip && !chip.disabled) {
        var index = parseInt(chip.dataset.mbQvOption, 10);
        quickView.selectedOptions[index] = chip.dataset.mbQvValue;
        quickView.touchedOptions[index] = true;
        renderOrUpdateReplacement();
        return;
      }

      var inc = e.target.closest('[data-mb-action="qv-qty-inc"]');
      if (inc) {
        quickView.quantity += 1;
        renderOrUpdateReplacement();
        return;
      }
      var dec = e.target.closest('[data-mb-action="qv-qty-dec"]');
      if (dec) {
        quickView.quantity = Math.max(1, quickView.quantity - 1);
        renderOrUpdateReplacement();
        return;
      }

      var addBtn = e.target.closest('[data-mb-action="qv-add"]');
      if (addBtn && !addBtn.disabled) {
        commitQuickViewSelection();
        return;
      }

      var quickViewTrigger = e.target.closest('[data-mb-action="quick-view"]');
      if (quickViewTrigger) {
        var card = quickViewTrigger.closest('[data-mb-context]');
        var bundleId = card ? card.getAttribute('data-mb-context') : (store.getActive() && store.getActive().id);
        open(quickViewTrigger.dataset.handle, bundleId);
      }
    });
  }

  /**
   * Adds a new pack item, or stages/updates a replacement while that flow
   * is open. The drawer stays open with the selected badge and View Pack
   * button; only View Pack commits a pending swap and returns to the pack.
   */
  function commitQuickViewSelection() {
    var product = quickView.product;
    // Every option with more than one value needs an actual click
    // before this can proceed - rather than silently falling back to
    // whatever firstAvailableOptionValues resolved, tell the shopper
    // what's missing so they don't end up adding a size they never
    // actually chose.
    var missingChoice = product.options.some(function (option, index) {
      return option.values && option.values.length > 1 && !quickView.touchedOptions[index];
    });
    if (missingChoice) {
      utils.dispatch('multibuy:toast', { message: 'Please select a size before adding', kind: 'size' });
      return;
    }

    var variant = pickVariant(product, quickView.selectedOptions);
    if (!variant || !variant.available) return;

    var item = {
      productId: product.id,
      productHandle: product.handle,
      productTitle: product.title,
      productImage: product.featured_image,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      compareAtPrice: variant.compare_at_price,
      quantity: quickView.quantity
    };

    if (quickView.bundleId) {
      var ctx = store.getContext(quickView.bundleId);
      if (ctx && ctx.replaceIndex != null) {
        setSelectedOption(ctx.id, product.id, quickView.selectedOptions[0]);
        MB.collection.stageReplacement(ctx.id, item);
        renderQuickView();
        return;
      }
      store.addItem(quickView.bundleId, item);
    }

    utils.dispatch('multibuy:quickview:added', { bundleId: quickView.bundleId, item: item });
    renderQuickView();
  }

  MB.product = {
    pickVariant: pickVariant,
    firstAvailableOptionValues: firstAvailableOptionValues,
    setSelectedOption: setSelectedOption,
    getSelectedOption: getSelectedOption,
    renderCard: renderCard,
    renderGrid: renderGrid,
    openQuickView: open,
    closeQuickView: close,
    bindQuickViewEvents: bindQuickViewEvents
  };
})(window, document);
